import React, {
  ChangeEventHandler,
  FocusEventHandler,
  ReactNode,
  Ref,
} from 'react';

import { useUniqueId } from 'utils/hooks';

import {
  InputAffixLabel,
  InputContainer,
  InputElement,
  InputPrefixVariant,
  InputStatus,
  InputStyleVariant,
} from './Input.styled';

type Props = {
  inputProps: {
    id?: string;
    name?: string;
    ref?: Ref<HTMLInputElement>;
    placeholder?: string;
    value?: string;
    disabled?: boolean;
    readOnly?: boolean;
    'data-qa-id'?: string;
    'aria-label'?: string;
    onChange?: ChangeEventHandler<HTMLInputElement>;
    onBlur?: FocusEventHandler<HTMLInputElement>;
    onFocus?: FocusEventHandler<HTMLInputElement>;
  };
  prefix?: ReactNode;
  suffix?: ReactNode;
  status?: InputStatus;
  prefixVariant?: InputPrefixVariant;
  styleVariant?: InputStyleVariant;
  fullHeight?: boolean;
};

export function Input({
  inputProps: {
    id,
    name,
    ref,
    placeholder,
    value,
    disabled,
    readOnly,
    onChange,
    onBlur,
    onFocus,
    'data-qa-id': dataQaId,
    'aria-label': ariaLabel,
  },
  prefix,
  suffix,
  status = 'neutral',
  prefixVariant = 'default',
  styleVariant = 'default',
  fullHeight,
}: Props) {
  const inputId = useUniqueId({ prefix: 'input-group-', override: id });

  return (
    <InputContainer
      $status={status}
      $prefixVariant={prefixVariant}
      $styleVariant={styleVariant}
      $fullHeight={Boolean(fullHeight)}
    >
      {prefix && <InputAffixLabel htmlFor={inputId}>{prefix}</InputAffixLabel>}

      <InputElement
        id={inputId}
        name={name}
        ref={ref}
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        onBlur={onBlur}
        onFocus={onFocus}
        disabled={disabled}
        readOnly={readOnly}
        data-qa-id={dataQaId}
        aria-label={ariaLabel}
      />

      {suffix && <InputAffixLabel htmlFor={inputId}>{suffix}</InputAffixLabel>}
    </InputContainer>
  );
}
